import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "@/components/atomic/Loading";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import { CategoricalAttributeFilter } from "@/components/ui/chat/attribute-filters/CategoricalAttributeFilter";
import { NumericalAttributeFilter } from "@/components/ui/chat/attribute-filters/NumericalAttributeFilter";
import { TextualAttributeFilter } from "@/components/ui/chat/attribute-filters/TextualAttributeFilter";
import { ChatAttributeFilter, ChatAttributeFilters } from "@/components/ui/chat/chatFilterUtils";
import { useAttributes } from "@/hooks/useAttributes";
import { useTags } from "@/hooks/useTags";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { cn } from "@/lib/utils";
import { Attribute, AttributeType } from "@/models/attribute";
import { useChatStore } from "@/stores/ui/useChatStore";

interface AttributesListProps {
  attributes: Attribute[] | undefined;
  isLoading: boolean;
  expandedAttributes: Set<string>;
  attributeFilters: ChatAttributeFilters;
  onToggleAttribute: (attributeName: string) => void;
  onFilterChange: (attributeName: string, filter: ChatAttributeFilter | null) => void;
}

const AttributesList: React.FC<AttributesListProps> = ({
  attributes,
  isLoading,
  expandedAttributes,
  attributeFilters,
  onToggleAttribute,
  onFilterChange,
}) => {
  const { t } = useTranslation("voiceek_ui");

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!attributes || attributes.length === 0) {
    return (
      <p className="text-[14px] text-[#999999] pt-2.5">
        {t("chat.filter.noAttributes")}
      </p>
    );
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      {attributes.map((attribute) => {
        const isExpanded = expandedAttributes.has(attribute.name);
        return (
          <div key={attribute.name} className="flex flex-col">
            <button
              onClick={() => onToggleAttribute(attribute.name)}
              className={cn(
                "flex items-center gap-2 text-left transition-colors p-1 -ml-1 rounded",
                "hover:cursor-pointer",
              )}
            >
              <p className="text-[14px] font-semibold text-[#333333] leading-[20px]">
                {attribute.name}
              </p>
              {isExpanded
                ? <IconChevronDown className="w-4 h-4 text-[#333333]" stroke={2} />
                : (
                  <IconChevronRight
                    className="w-4 h-4 text-[#333333] group-hover:translate-x-0.5 transition-transform"
                    stroke={2}
                  />
                )}
            </button>

            <div
              className={cn(
                "grid transition-all duration-200 ease-in-out",
                isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
              )}
            >
              <div className="overflow-hidden">
                {attribute.type === AttributeType.CATEGORICAL && (
                  <CategoricalAttributeFilter
                    attribute={attribute}
                    currentFilter={attributeFilters[attribute.name]}
                    onFilterChange={onFilterChange}
                  />
                )}
                {attribute.type === AttributeType.NUMERICAL && (
                  <NumericalAttributeFilter
                    attribute={attribute}
                    currentFilter={attributeFilters[attribute.name]}
                    onFilterChange={onFilterChange}
                  />
                )}
                {attribute.type === AttributeType.TEXTUAL && (
                  <TextualAttributeFilter
                    attribute={attribute}
                    currentFilter={attributeFilters[attribute.name]}
                    onFilterChange={onFilterChange}
                  />
                )}
                <div className="h-px bg-[#dddddd]" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const AdvancedFilterTab: React.FC = () => {
  const { t } = useTranslation("voiceek_ui");
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const { attributes, isLoading } = useAttributes(sessionCurrentWorkspaceId, true);
  const { tags, isLoading: isLoadingTags } = useTags(sessionCurrentWorkspaceId || "");

  const { attributeFilters, tagIds } = useChatStore((s) => {
    return s.getCurrentConversationState();
  });
  const setCurrentConversationState = useChatStore((s) => s.setCurrentConversationState);

  // フィルタが設定されている属性を初期状態で展開する
  const initialExpandedAttributes = useMemo(() => {
    return new Set(Object.keys(attributeFilters));
  }, [attributeFilters]);

  const [expandedAttributes, setExpandedAttributes] = useState<Set<string>>(initialExpandedAttributes);

  const toggleAttribute = (attributeName: string) => {
    setExpandedAttributes((prev) => {
      const next = new Set(prev);
      if (next.has(attributeName)) {
        next.delete(attributeName);
      } else {
        next.add(attributeName);
      }
      return next;
    });
  };

  const toggleTag = (tagId: string) => {
    const currentTagIds = tagIds || [];
    if (currentTagIds.includes(tagId)) {
      setCurrentConversationState({ tagIds: currentTagIds.filter((id) => id !== tagId) });
    } else {
      setCurrentConversationState({ tagIds: [...currentTagIds, tagId] });
    }
  };

  const handleFilterChange = (attributeName: string, filter: ChatAttributeFilter | null) => {
    if (filter) {
      setCurrentConversationState({
        attributeFilters: { ...attributeFilters, [attributeName]: filter },
      });
    } else {
      const { [attributeName]: _, ...restAttr } = attributeFilters;
      setCurrentConversationState({ attributeFilters: restAttr });
    }
  };

  return (
    <ScrollArea className="h-full">
      <div className="flex gap-6 pr-6 min-h-10">
        <div className="flex items-start pt-[10px]  w-[60px]">
          <p className="text-[14px] font-semibold text-[#333333]">
            {t("chat.filter.attributes")}
          </p>
        </div>

        <div className="flex items-stretch self-stretch">
          <div className="w-[2px] bg-[#d9d9d9]" />
        </div>

        <AttributesList
          attributes={attributes}
          isLoading={isLoading}
          expandedAttributes={expandedAttributes}
          attributeFilters={attributeFilters}
          onToggleAttribute={toggleAttribute}
          onFilterChange={handleFilterChange}
        />
      </div>

      {/* タグセクション */}
      {!!tags?.length
        && (
          <div className="flex gap-6 pr-6 mt-8">
            <div className="flex items-start pt-[10px] w-[60px]">
              <p className="text-[14px] font-semibold text-[#333333]">
                タグ
              </p>
            </div>

            <div className="flex items-stretch self-stretch">
              <div className="w-[2px] bg-[#d9d9d9]" />
            </div>

            <div className="basis-0 grow flex flex-col items-start justify-center min-h-px min-w-px">
              {isLoadingTags
                ? <LoadingSpinner />
                : (
                  <div className="content-start flex flex-wrap gap-2 items-start w-full">
                    {tags.map((tag) => (
                      <button
                        key={tag.id}
                        onClick={() => toggleTag(tag.id)}
                        className={cn(
                          "border px-[16px] py-[4px] rounded-[13px] text-[14px] leading-[16px] text-left transition-colors hover:cursor-pointer",
                          tagIds?.includes(tag.id)
                            ? "bg-[#6888e1] text-white border-[#6888e1]"
                            : "border-[#5f6368] text-[#333]",
                        )}
                      >
                        {tag.name}
                      </button>
                    ))}
                    {(!tags || tags.length === 0) && (
                      <p className="text-[14px] text-[#999999]">
                        {t("chat.filter.noTags")}
                      </p>
                    )}
                  </div>
                )}
            </div>
          </div>
        )}

      <ScrollBar orientation="vertical" />
    </ScrollArea>
  );
};
